// Slides in after the user picks a type — replaces the navigate() call entirely.

import { useForm, Controller } from "react-hook-form";
import React, { useRef, useContext, useState, useEffect, useCallback } from "react";
import moment from 'moment';
import { accountService } from "../../services/accountService";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import LogicContext from "../../Context/LogicContext";
import GeneralCustomerSearch from "../../generals/GeneralCustomerSearch";
import { SetPaymentMadeAtom, GetPaymentReceiveAtom } from "../../store/paymentReceivedAtom";
import { debounce } from 'lodash';


// ─── Icons ────────────────────────────────────────────────────────────────────
const PrintIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
);
const ChevronDown = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);
const ChevronLeft = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
);
const SearchIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0" />
    </svg>
);
const XIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);
const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);
const UserIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);


const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition";
const selectCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none";


// ─── Label + Input helpers ─────────────────────────────────────────────────────
const FormField = ({ label, children, required }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);







export const CreatePaymentReceivedPanel = ({ isOpen, onClose, onBack, selectedType, bankName, accountId, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const [selCustomer, setSelCustomer] = useState(null);
    const isMounted = useRef(false);

    const myPromiseModal = createModal(GeneralModal);
    const logicContext = useContext(LogicContext);
    const paymentAtom = GetPaymentReceiveAtom();
    const setPaymentMadeAtom = SetPaymentMadeAtom();

    const { register, handleSubmit, reset, control, getValues, setValue, formState: { errors } } = useForm({
        defaultValues: {
            payment_date: moment().format('YYYY-MM-DD'),
            customer_id: "",
            payment_method: "",
            payment_refno: "",
            amount: "",
            remark: "",
        }
    });

    // Load payment list data if not available
    useEffect(() => {
        if (!paymentAtom?.deposit_info?.length) {
            getPaymentListApi();
        }
    }, [paymentAtom?.deposit_info?.length]);

    // Reset form and state when panel opens OR closes
    useEffect(() => {
        if (isOpen) {
            // Opening fresh form
            reset({
                payment_date: moment().format('YYYY-MM-DD'),
                customer_id: "",
                payment_method: "",
                payment_refno: "",
                amount: "",
                remark: "",
            });
            setSelCustomer(null);
        } else if (!isOpen) {
            // Cleaning up when panel is closed
            reset({
                payment_date: moment().format('YYYY-MM-DD'),
                customer_id: "",
                payment_method: "",
                payment_refno: "",
                amount: "",
                remark: "",
            });
            setSelCustomer(null);
        }
    }, [isOpen]);

    const getPaymentListApi = async () => {
        try {
            const response = await accountService.getPaymentReceiveList();
            if (response?.data?.deposit_info?.length) {
                setPaymentMadeAtom(response.data);
            }
        } catch (error) {
            // logicContext.showToast("connection error getting invoice data", "error");
        }
    };

    const debouncedOnCustomerSelect = useCallback(
        debounce((customer) => {
            setSelCustomer(customer);
            setValue("customer_id", customer.Customer_Id, { shouldValidate: true });
        }, 300),
        [setValue]
    );

    const onCustomerSelect = useCallback((customer) => {
        debouncedOnCustomerSelect(customer);
    }, [debouncedOnCustomerSelect]);

    const depositMethods = paymentAtom?.deposit_method?.filter(m => m.Name !== 'All');

    const onSubmit = async (data) => {
        const formData = getValues();

        const result = await myPromiseModal({
            title: 'Confirmation',
            body: 'Save Payment Records',
            showInput: false,
        });

        if (!result?.decision) return;

        setSaving(true);

        try {
            const response = await accountService.submitPaymentReceived({ ...formData, option: 1 });
            const cleanResponse = logicContext.httpResponseManager(response.data?.[0] || response.data?.result_status?.[0]);
            logicContext.showToast(cleanResponse?.message_text, 'success');
            // Clear state immediately on success
            reset({
                payment_date: moment().format('YYYY-MM-DD'),
                customer_id: "",
                payment_method: "",
                payment_refno: "",
                amount: "",
                remark: "",
            });
            setSelCustomer(null);
            onSaved?.();
            onClose();
        } catch (err) {
            console.error("Failed to save payment", err);
            logicContext.showToast("Error creating payment record", "error");
        } finally {
            setSaving(false);
        }
    };



    return (
        <div
            className={`fixed top-0 right-0 h-screen w-[480px] bg-white z-50 flex flex-col overflow-hidden
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            style={{ borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 24px rgba(0,0,0,0.10)' }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button
                        onClick={onBack}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                        title="Back to type selection"
                    >
                        <ChevronLeft />
                    </button>
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest leading-none mb-0.5">
                            New Payment Received
                        </p>
                        <h3 className="text-base font-bold text-gray-800 leading-tight">
                            {selectedType?.transaction_name || 'Add Payment'}
                        </h3>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                >
                    <XIcon />
                </button>
            </div>

            {/* From bank context pill */}
            {bankName && (
                <div className="px-5 pt-4 pb-1 flex-shrink-0">
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        <span className="text-xs text-blue-700 font-medium">
                            From: <span className="font-bold">{bankName || 'Selected Account'}</span>
                        </span>
                    </div>
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                {/* Customer Name - Custom Search Component */}
                <FormField label="Customer Name" required>
                    <Controller
                        name="customer_id"
                        control={control}
                        rules={{
                            required: "Customer selection is required",
                            validate: (value) => value !== 0 || "Please select a valid customer"
                        }}
                        render={({ field }) => (
                            <GeneralCustomerSearch
                                onCustomerSelect={onCustomerSelect}
                            />
                        )}
                    />
                    {errors.customer_id && <p className="text-xs text-red-500 mt-1">{errors.customer_id.message}</p>}

                    {/* Selected Customer Info Card */}
                    {selCustomer && (
                        <div className="mt-3 p-3 border border-green-200 rounded-lg bg-green-50">
                            <div className="flex items-center gap-2 mb-1">
                                <UserIcon />
                                <span className="text-sm font-semibold text-green-800">{selCustomer?.Name}</span>
                            </div>
                            <p className="text-xs text-green-600">
                                ID: {selCustomer?.Customer_Id}
                            </p>
                        </div>
                    )}
                </FormField>

                {/* Payment Date */}
                <FormField label="Payment Date" required>
                    <input
                        type="date"
                        className={inputCls}
                        {...register('payment_date', { required: 'Payment date is required' })}
                    />
                    {errors.payment_date && <p className="text-xs text-red-500 mt-1">{errors.payment_date.message}</p>}
                </FormField>

                {/* Payment Method */}
                <FormField label="Payment Method" required>
                    <select
                        className={selectCls}
                        {...register('payment_method', { required: 'Payment method is required' })}
                    >
                        <option value="">Select payment method</option>
                        {depositMethods?.map((method) => (
                            <option key={method?.Code} value={method?.Code}>
                                {method?.Name}
                            </option>
                        ))}
                    </select>
                    {errors.payment_method && <p className="text-xs text-red-500 mt-1">{errors.payment_method.message}</p>}
                </FormField>

                {/* Payment Reference Number */}
                <FormField label="Payment Reference Number" required>
                    <input
                        type="text"
                        placeholder="Enter Reference Number"
                        className={inputCls}
                        {...register('payment_refno', { required: 'Payment reference number is required' })}
                    />
                    {errors.payment_refno && <p className="text-xs text-red-500 mt-1">{errors.payment_refno.message}</p>}
                </FormField>

                {/* Amount */}
                <FormField label="Amount" required>
                    <div className="flex">
                        <span className="inline-flex items-center px-3 py-2.5 border border-r-0 border-gray-200 rounded-l-lg bg-gray-50 text-sm font-medium text-gray-500">
                            NGN
                        </span>
                        <input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            className={`${inputCls} rounded-l-none`}
                            {...register('amount', {
                                required: 'Amount is required',
                                min: { value: 0.01, message: 'Amount must be greater than 0' },
                                valueAsNumber: true,
                            })}
                        />
                    </div>
                    {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount.message}</p>}
                </FormField>

                {/* Remark */}
                <FormField label="Remark">
                    <textarea
                        rows={3}
                        placeholder="Enter any remarks"
                        className={`${inputCls} resize-none`}
                        {...register('remark')}
                    />
                </FormField>

                {/* spacer so last field isn't hidden behind sticky footer */}
                <div className="h-2" />
            </form>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
                <button
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                    disabled={saving}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition shadow-sm"
                >
                    {saving ? 'Saving…' : 'Submit Payment'}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold py-2.5 px-4 rounded-lg border border-gray-200 transition"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};